import type { SiteDto, SkuDto } from '@pl/domain';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAvailability } from '../../lib/queries';
import { skuMatches } from '../../lib/skuSearch';
import { SkuTag, Swatch } from '../../ui/bits';
import s from './NewOrder.module.css';

const MAX_MATCHES = 8;

function highlight(code: string, query: string): ReactNode {
  const i = code.toLowerCase().indexOf(query.toLowerCase());
  if (!query || i < 0) return code;
  return (
    <>
      {code.slice(0, i)}
      <mark>{code.slice(i, i + query.length)}</mark>
      {code.slice(i + query.length)}
    </>
  );
}

export function SkuPicker({
  skus,
  sites,
  value,
  onSelect,
  lineTag,
}: {
  skus: SkuDto[];
  /** All sites shown as columns in the dropdown, in display order (live and not-live). */
  sites: SiteDto[];
  value: SkuDto | null;
  onSelect: (sku: SkuDto) => void;
  /** e.g. "line 02" — this SKU is already used elsewhere on the order. */
  lineTag: (skuId: string) => string | null;
}) {
  const id = useId();
  const [editing, setEditing] = useState(!value);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  // Escape dismisses the list without touching the query, so the user can keep typing.
  const [dismissed, setDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // A line that lost its SKU (shouldn't normally happen) re-arms edit mode.
  useEffect(() => {
    if (!value) setEditing(true);
  }, [value]);

  /** Every hit, uncapped — the header's count has to be the true one, not the post-cap one. */
  const allMatches = useMemo(() => {
    if (!query.trim()) return [];
    return skus.filter((sku) => skuMatches(sku, query));
  }, [skus, query]);
  const matches = useMemo(() => allMatches.slice(0, MAX_MATCHES), [allMatches]);

  const matchIds = useMemo(() => matches.map((m) => m.id), [matches]);
  const availabilityQuery = useAvailability(matchIds);
  const availByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of availabilityQuery.data ?? []) m.set(`${row.siteId}:${row.skuId}`, row.available);
    return m;
  }, [availabilityQuery.data]);

  const open = editing && !dismissed && matches.length > 0;
  const listboxId = `${id}-listbox`;

  function choose(sku: SkuDto) {
    onSelect(sku);
    setQuery('');
    setActiveIndex(-1);
    setDismissed(false);
    setEditing(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      // ArrowDown brings a dismissed list back without retyping.
      if (e.key === 'ArrowDown' && dismissed && matches.length > 0) {
        e.preventDefault();
        setDismissed(false);
        setActiveIndex(0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // While the list is open Enter belongs to the list, modifiers and all. Stopping the event
      // here keeps ⌘/Ctrl+Enter from also reaching the page's save shortcut, which would save an
      // order without the line this keystroke is still in the middle of choosing.
      e.preventDefault();
      e.stopPropagation();
      if (activeIndex >= 0 && matches[activeIndex]) choose(matches[activeIndex]!);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDismissed(true);
      setActiveIndex(-1);
    }
  }

  if (!editing && value) {
    return (
      <button
        type="button"
        className={`inp ${s.chosenBtn}`}
        style={{ width: '100%', justifyContent: 'flex-start', cursor: 'text' }}
        onClick={() => {
          setEditing(true);
          setQuery('');
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        <SkuTag sku={value} short withCode />
      </button>
    );
  }

  return (
    <div className="anchor" style={{ position: 'relative' }}>
      <div className={`inp${open ? ' focus' : ''}`}>
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${id}-opt-${activeIndex}` : undefined}
          aria-label="SKU"
          autoComplete="off"
          placeholder="Type a code, color or cable type"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
            setDismissed(false);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (value) setEditing(false);
          }}
          style={{ border: 0, outline: 'none', font: 'inherit', color: 'inherit', background: 'transparent', flex: 1, minWidth: 0, padding: 0 }}
        />
      </div>
      {open && (
        <div className={`menu ${s.menu}`} role="presentation" style={{ '--site-count': sites.length } as CSSProperties}>
          <div className={`menu-h ${s.cols}`}>
            <span className="row" style={{ gap: 12 }}>
              <span>
                {allMatches.length > MAX_MATCHES ? `Showing ${MAX_MATCHES} of ${allMatches.length}` : `${allMatches.length} ${allMatches.length === 1 ? 'match' : 'matches'}`} &ldquo;{query}&rdquo;
              </span>
              <span className="hint" style={{ fontSize: 11 }}>
                <span className="kbd inl">↑↓</span>choose
              </span>
              <span className="hint" style={{ fontSize: 11 }}>
                <span className="kbd inl">↵</span>add
              </span>
              <span className="hint" style={{ fontSize: 11 }}>
                <span className="kbd inl">esc</span>close
              </span>
            </span>
            {sites.map((site) => (
              <span key={site.id} className={s.colHead}>
                {site.shortCode}
              </span>
            ))}
          </div>
          <ul role="listbox" id={listboxId} style={{ listStyle: 'none', margin: 0, padding: '4px 0', maxHeight: 320, overflowY: 'auto' }}>
            {matches.map((sku, i) => {
              const tag = lineTag(sku.id);
              return (
                <li
                  key={sku.id}
                  role="option"
                  id={`${id}-opt-${i}`}
                  aria-selected={i === activeIndex}
                  className={`${s.opt} ${s.cols}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(sku);
                  }}
                >
                  <span className="sku">
                    <Swatch hex={sku.colourHex} name={sku.colourName} />
                    <b>{sku.name}</b>
                    {sku.colourName && <span className="clr">{sku.colourName}</span>}
                    <span className="code">{highlight(sku.code, query)}</span>
                    {tag && <span className={s.optTag}>{tag}</span>}
                  </span>
                  {sites.map((site) => {
                    if (!site.live)
                      return (
                        <span key={site.id} className={`${s.colVal} faint`}>
                          —
                        </span>
                      );
                    // Same rule as the page: absent from a successful response is a real 0, but
                    // a response that has not arrived is unknown and must not read as 0.
                    if (!availabilityQuery.isSuccess)
                      return (
                        <span key={site.id} className={`${s.colVal} faint`}>
                          —
                        </span>
                      );
                    const av = availByKey.get(`${site.id}:${sku.id}`) ?? 0;
                    return (
                      <span key={site.id} className={`${s.colVal}${av < 0 ? ' neg' : ''}`}>
                        {av}
                      </span>
                    );
                  })}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
